import React, { useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { StackScreenProps } from '@react-navigation/stack';
import {
  getReceivedInterests, getSentInterests, respondToInterest,
  getShortlist, getContactsViewed, getViewedProfiles,
} from '../services/api';
import { ProfileCard, THEME } from '../components';
import type {
  Profile, InterestResponse, ShortlistResponse, ContactViewedResponse,
  ViewedProfileResponse, RootStackParamList,
} from '../types';

type Props = StackScreenProps<RootStackParamList, 'Main'>;
type Tab = 'received' | 'sent' | 'saved' | 'contacts' | 'viewed';

const toHttps = (url?: string) => url && !url.startsWith('http') ? `https://${url}` : url;

const STATUS_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  PENDING:  { bg: '#FEF9E7', text: '#B7950B', label: 'Pending' },
  ACCEPTED: { bg: '#EAFAF1', text: '#1E8449', label: 'Accepted' },
  REJECTED: { bg: '#FDEDEC', text: '#922B21', label: 'Rejected' },
};

// ─── Interest Row ─────────────────────────────────────────────────────────────

interface InterestRowProps {
  item: InterestResponse;
  mode: Tab;
  onAccept?: (id: string) => void;
  onReject?: (id: string) => void;
  onPress: (profileId: string) => void;
  responding: string | null;
}

const InterestRow: React.FC<InterestRowProps> = ({
  item, mode, onAccept, onReject, onPress, responding,
}) => {
  const s = STATUS_STYLE[item.status] ?? STATUS_STYLE.PENDING;
  const imageUri = toHttps(item.photoUrl);

  return (
    <TouchableOpacity style={styles.row} onPress={() => onPress(item.profileId)} activeOpacity={0.82}>
      {imageUri
        ? <Image source={{ uri: imageUri }} style={styles.avatar} />
        : <View style={styles.avatarPlaceholder}>
            <Text style={styles.avatarInitial}>{item.name?.charAt(0)?.toUpperCase() ?? '?'}</Text>
          </View>
      }
      <View style={{ flex: 1 }}>
        <Text style={styles.name}>{item.name ?? 'Unknown'}</Text>
        {!!(item.district || item.stateName) && (
          <Text style={styles.location}>
            {[item.district, item.stateName].filter(Boolean).join(', ')}
          </Text>
        )}
        <Text style={styles.date}>{new Date(item.sentAt).toLocaleDateString('en-IN')}</Text>
      </View>

      {mode === 'received' && item.status === 'PENDING' ? (
        <View style={styles.actionBtns}>
          <TouchableOpacity
            style={styles.acceptBtn}
            disabled={responding === item.id}
            onPress={() => onAccept?.(item.id)}>
            {responding === item.id
              ? <ActivityIndicator size="small" color="#fff" />
              : <Icon name="checkmark" size={18} color="#fff" />
            }
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.rejectBtn}
            disabled={responding === item.id}
            onPress={() => onReject?.(item.id)}>
            <Icon name="close" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      ) : (
        <View style={[styles.statusBadge, { backgroundColor: s.bg }]}>
          <Text style={[styles.statusText, { color: s.text }]}>{s.label}</Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────

const TABS: { key: Tab; label: string }[] = [
  { key: 'received', label: 'Received' },
  { key: 'sent',     label: 'Sent'     },
  { key: 'saved',    label: 'Shortlisted' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'viewed',   label: 'Visited'  },
];

const InterestsScreen: React.FC<Props> = ({ navigation }) => {
  const [tab, setTab]           = useState<Tab>('received');
  const [received, setReceived] = useState<InterestResponse[]>([]);
  const [sent, setSent]         = useState<InterestResponse[]>([]);
  const [saved, setSaved]       = useState<ShortlistResponse[]>([]);
  const [contacts, setContacts] = useState<ContactViewedResponse[]>([]);
  const [loading, setLoading]   = useState(true);
  const [responding, setResponding] = useState<string | null>(null);

  // Viewed-profiles paging state
  const [viewedProfiles, setViewedProfiles]       = useState<ViewedProfileResponse[]>([]);
  const [viewedHasMore, setViewedHasMore]         = useState(false);
  const [viewedPage, setViewedPage]               = useState(0);
  const [viewedLoadingMore, setViewedLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      getReceivedInterests(),
      getSentInterests(),
      getShortlist(),
      getContactsViewed(),
      getViewedProfiles(0),
    ])
      .then(([r, s, sh, cv, vp]) => {
        if (r.status === 'fulfilled') setReceived(Array.isArray(r.value.data) ? r.value.data : []);
        if (s.status === 'fulfilled') setSent(Array.isArray(s.value.data) ? s.value.data : []);
        if (sh.status === 'fulfilled') setSaved(Array.isArray(sh.value.data) ? sh.value.data : []);
        if (cv.status === 'fulfilled') setContacts(Array.isArray(cv.value.data) ? cv.value.data : []);
        if (vp.status === 'fulfilled') {
          setViewedProfiles(vp.value.data.profiles ?? []);
          setViewedHasMore(vp.value.data.hasMore ?? false);
          setViewedPage(0);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useFocusEffect(load);

  const loadMoreViewed = useCallback(() => {
    if (!viewedHasMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setViewedLoadingMore(true);
    const nextPage = viewedPage + 1;
    getViewedProfiles(nextPage)
      .then(res => {
        setViewedProfiles(prev => [...prev, ...(res.data.profiles ?? [])]);
        setViewedHasMore(res.data.hasMore ?? false);
        setViewedPage(nextPage);
      })
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load more profiles' }))
      .finally(() => {
        setViewedLoadingMore(false);
        loadingMoreRef.current = false;
      });
  }, [viewedHasMore, viewedPage]);

  const handleRespond = async (id: string, status: 'ACCEPTED' | 'REJECTED') => {
    setResponding(id);
    try {
      await respondToInterest(id, status);
      setReceived(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      Toast.show({ type: 'success', text1: status === 'ACCEPTED' ? 'Interest accepted!' : 'Interest rejected' });
    } catch (e: any) {
      Toast.show({ type: 'error', text1: 'Failed', text2: (e as Error).message });
    } finally {
      setResponding(null);
    }
  };

  const navigate = (profileId: string) =>
    (navigation as any).navigate('ProfileDetail', { profileId });

  const pendingCount = received.filter(i => i.status === 'PENDING').length;
  const list = tab === 'received' ? received : tab === 'sent' ? sent : [];

  return (
    <SafeAreaView style={styles.container} edges={['top']}>

      {/* ── Tab Pills ── */}
      <View style={styles.tabs}>
        {TABS.map(({ key, label }) => {
          const badge = key === 'received' ? pendingCount : 0;
          return (
            <TouchableOpacity
              key={key}
              style={[styles.tab, tab === key && styles.tabActive]}
              onPress={() => setTab(key)}>
              <View>
                <Text style={[styles.tabText, tab === key && styles.tabTextActive]}>{label}</Text>
                {badge > 0 && (
                  <View style={styles.tabBadge}>
                    <Text style={styles.tabBadgeText}>{badge > 9 ? '9+' : String(badge)}</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={THEME.primary} />
        </View>

      ) : tab === 'viewed' ? (
        /* ── Visited Profiles ── */
        viewedProfiles.length === 0 ? (
          <View style={styles.center}>
            <Icon name="eye-outline" size={48} color={THEME.border} />
            <Text style={styles.emptyText}>
              No profiles visited yet{'\n'}Profiles you visit will appear here
            </Text>
          </View>
        ) : (
          <FlatList
            data={viewedProfiles}
            keyExtractor={item => item.profileId}
            contentContainerStyle={{ padding: 12, paddingBottom: 80 }}
            onEndReached={loadMoreViewed}
            onEndReachedThreshold={0.5}
            ListFooterComponent={
              viewedLoadingMore ? (
                <ActivityIndicator size="large" color={THEME.primary} style={{ paddingVertical: 20 }} />
              ) : !viewedHasMore && viewedProfiles.length > 0 ? (
                <View style={[styles.center, { paddingVertical: 24 }]}>
                  <Icon name="checkmark-circle-outline" size={40} color={THEME.border} />
                  <Text style={styles.emptyText}>That's all your visited profiles</Text>
                </View>
              ) : null
            }
            renderItem={({ item }) => (
              <ProfileCard
                profile={{
                  id: item.profileId,
                  profileCode: item.profileCode,
                  name: item.name ?? '',
                  thumbnailUrl: item.thumbnailUrl,
                  highestEducation: item.highestEducation,
                  job: item.job,
                  district: item.district,
                  stateName: item.stateName,
                  religion: item.religion,
                  caste: item.caste,
                } as Profile}
                onPress={() => navigate(item.profileId)}
              />
            )}
          />
        )

      ) : tab === 'contacts' ? (
        /* ── Contacts Viewed: list rows ── */
        contacts.length === 0 ? (
          <View style={styles.center}>
            <Icon name="call-outline" size={48} color={THEME.border} />
            <Text style={styles.emptyText}>
              No contacts viewed yet{'\n'}View a profile's contact details to see them here
            </Text>
          </View>
        ) : (
          <FlatList
            data={contacts}
            keyExtractor={item => item.profileId}
            contentContainerStyle={{ padding: 16 }}
            renderItem={({ item }) => {
              const imageUri = toHttps(item.photoUrl);
              return (
                <TouchableOpacity
                  style={styles.row}
                  activeOpacity={0.82}
                  onPress={() => navigate(item.profileId)}>
                  {imageUri
                    ? <Image source={{ uri: imageUri }} style={styles.avatar} />
                    : <View style={styles.avatarPlaceholder}>
                        <Text style={styles.avatarInitial}>{item.name?.charAt(0)?.toUpperCase() ?? '?'}</Text>
                      </View>
                  }
                  <View style={{ flex: 1 }}>
                    <Text style={styles.name}>{item.name ?? 'Unknown'}</Text>
                    {!!(item.district || item.stateName) && (
                      <Text style={styles.location}>
                        {[item.district, item.stateName].filter(Boolean).join(', ')}
                      </Text>
                    )}
                    {!!item.profileCode && <Text style={styles.date}>{item.profileCode}</Text>}
                  </View>
                  <Icon name="call-outline" size={18} color={THEME.primary} />
                </TouchableOpacity>
              );
            }}
          />
        )

      ) : tab === 'saved' ? (
        /* ── Shortlisted ── */
        saved.length === 0 ? (
          <View style={styles.center}>
            <Icon name="checkmark-circle-outline" size={48} color={THEME.border} />
            <Text style={styles.emptyText}>
              No shortlisted profiles yet{'\n'}Tap "Shortlist" on any profile to add it here
            </Text>
          </View>
        ) : (
          <FlatList
            data={saved}
            keyExtractor={item => item.id}
            contentContainerStyle={{ padding: 16 }}
            renderItem={({ item }) => {
              const imageUri = toHttps(item.photoUrl);
              return (
                <TouchableOpacity
                  style={styles.row}
                  activeOpacity={0.82}
                  onPress={() => navigate(item.profileId)}>
                  {imageUri
                    ? <Image source={{ uri: imageUri }} style={styles.avatar} />
                    : <View style={styles.avatarPlaceholder}>
                        <Text style={styles.avatarInitial}>{item.name?.charAt(0)?.toUpperCase() ?? '?'}</Text>
                      </View>
                  }
                  <View style={{ flex: 1 }}>
                    <Text style={styles.name}>{item.name ?? 'Unknown'}</Text>
                    {!!(item.district || item.stateName) && (
                      <Text style={styles.location}>
                        {[item.district, item.stateName].filter(Boolean).join(', ')}
                      </Text>
                    )}
                    <Text style={styles.date}>
                      Shortlisted {new Date(item.shortlistedAt).toLocaleDateString('en-IN')}
                    </Text>
                  </View>
                  <Icon name="checkmark-circle" size={20} color={THEME.primary} />
                </TouchableOpacity>
              );
            }}
          />
        )

      ) : list.length === 0 ? (
        /* ── Received / Sent empty state ── */
        <View style={styles.center}>
          <Icon name="heart-outline" size={48} color={THEME.border} />
          <Text style={styles.emptyText}>
            {tab === 'received' ? 'No interests received yet' : "You haven't sent any interests yet"}
          </Text>
        </View>

      ) : (
        /* ── Received / Sent list ── */
        <FlatList
          data={list}
          keyExtractor={item => item.id}
          contentContainerStyle={{ padding: 16 }}
          renderItem={({ item }) => (
            <InterestRow
              item={item}
              mode={tab}
              responding={responding}
              onAccept={id => handleRespond(id, 'ACCEPTED')}
              onReject={id => handleRespond(id, 'REJECTED')}
              onPress={id => navigate(id)}
            />
          )}
        />
      )}
    </SafeAreaView>
  );
};

export default InterestsScreen;

// ─── Styles: Screen ───────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: THEME.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  emptyText: { fontSize: 15, color: THEME.textLight, textAlign: 'center', lineHeight: 22 },

  tabs: {
    flexDirection: 'row',
    backgroundColor: THEME.card,
    marginHorizontal: 16,
    marginVertical: 10,
    borderRadius: 12,
    padding: 4,
    shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 6, elevation: 2,
  },
  tab: {
    flex: 1, paddingVertical: 9, borderRadius: 10, alignItems: 'center',
  },
  tabActive: { backgroundColor: THEME.primary },
  tabText: { fontSize: 11, fontWeight: '700', color: THEME.textLight },
  tabTextActive: { color: '#fff' },
  tabBadge: {
    position: 'absolute', top: -5, right: -8,
    minWidth: 15, height: 15, borderRadius: 8,
    backgroundColor: '#E53935', alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 3,
  },
  tabBadgeText: { fontSize: 9, fontWeight: '900', color: '#fff' },

  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: THEME.card, borderRadius: 14, padding: 14, marginBottom: 10,
    shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 6, elevation: 2,
  },
  avatar: { width: 72, height: 72, borderRadius: 12, backgroundColor: '#EDE0D8' },
  avatarPlaceholder: {
    width: 72, height: 72, borderRadius: 12,
    backgroundColor: THEME.primary, alignItems: 'center', justifyContent: 'center',
  },
  avatarInitial: { fontSize: 28, fontWeight: '900', color: '#fff' },
  name: { fontSize: 16, fontWeight: '700', color: THEME.text },
  location: { fontSize: 13, color: THEME.textLight, marginTop: 2 },
  date: { fontSize: 12, color: THEME.textLight, marginTop: 4 },
  actionBtns: { flexDirection: 'row', gap: 8 },
  acceptBtn: {
    width: 38, height: 38, borderRadius: 19,
    backgroundColor: THEME.success, alignItems: 'center', justifyContent: 'center',
  },
  rejectBtn: {
    width: 38, height: 38, borderRadius: 19,
    backgroundColor: THEME.danger, alignItems: 'center', justifyContent: 'center',
  },
  statusBadge: {
    paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20, alignSelf: 'center',
  },
  statusText: { fontSize: 13, fontWeight: '700' },
});
